// GA4 + Google Consent Mode v2 (basic mode: gtag.js loads only after analytics consent).
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type ConsentState = { analytics: boolean; decidedAt: string; version: 1 };
const STORAGE_KEY = "interoform-consent";
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

let loaded = false;
let loading: Promise<void> | null = null;
let measurementId: string | null = null;
let lastPagePath: string | null = null;
let pendingPage: string | null = null;
const firedLeads = new Set<string>();
const pendingLeads = new Map<string, "kitchen" | "furniture" | "contact">();

export function readConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && v.version === 1 && typeof v.analytics === "boolean" ? v : null;
  } catch {
    return null;
  }
}

function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params -- gtag.js needs the Arguments object
  window.dataLayer.push(arguments);
}

function deleteGaCookies() {
  const host = location.hostname;
  const domains = ["", host, `.${host}`, `.${host.replace(/^www\./, "")}`];
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (name === "_ga" || name.startsWith("_ga_") || name === "_gid") {
      domains.forEach((d) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
      });
    }
  });
}

async function initializeGa() {
  if (loaded) return;
  let id: unknown;
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/ga-config`, { headers: { apikey: ANON } });
    if (!res.ok) throw new Error(`GA configuration request failed (${res.status})`);
    const data = await res.json();
    id = data?.measurementId;
  } catch {
    console.warn("Analytics configuration unavailable; initialization can be retried.");
    return;
  }
  // Consent may have been withdrawn while the public configuration was loading.
  if (!readConsent()?.analytics || typeof id !== "string" || !/^G-[A-Z0-9]+$/i.test(id)) return;
  measurementId = id;
  loaded = true;

  window.gtag = gtag;
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
  gtag("consent", "update", { analytics_storage: "granted" });
  gtag("set", "developer_id.dZjgwMW", true);
  gtag("js", new Date());
  gtag("config", measurementId, { send_page_view: false, anonymize_ip: true });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  s.onerror = () => {
    s.remove();
    loaded = false;
    measurementId = null;
    window.gtag = undefined;
    window.dataLayer = [];
    lastPagePath = null;
    console.warn("Analytics script unavailable; initialization can be retried.");
  };
  document.head.appendChild(s);

  if (pendingPage) {
    const p = pendingPage;
    pendingPage = null;
    lastPagePath = null;
    trackPageView(p);
  }
  for (const [submissionId, leadType] of pendingLeads) trackLead(submissionId, leadType);
}

function loadGa(): Promise<void> {
  if (loaded || !readConsent()?.analytics) return Promise.resolve();
  if (loading) return loading;
  loading = initializeGa().finally(() => { loading = null; });
  return loading;
}

export function setConsent(analytics: boolean) {
  const previouslyGranted = !!readConsent()?.analytics;
  const state: ConsentState = { analytics, decidedAt: new Date().toISOString(), version: 1 };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  if (analytics) {
    if (loaded && window.gtag) window.gtag("consent", "update", { analytics_storage: "granted" });
    if (!previouslyGranted) resetPageDedupe();
    trackPageView(location.pathname); // also counts the current page on re-consent
  } else {
    if (window.gtag) window.gtag("consent", "update", { analytics_storage: "denied" });
    deleteGaCookies();
    pendingPage = null;
    pendingLeads.clear();
  }
  window.dispatchEvent(new CustomEvent("interoform-consent", { detail: state }));
}

export function initAnalytics() {
  if (readConsent()?.analytics) void loadGa();
}

function canSend() {
  return !!readConsent()?.analytics && !!window.gtag && !!measurementId;
}

/** Sends one page_view per distinct navigation (dedupes React re-renders/StrictMode). */
export function trackPageView(path: string) {
  if (!readConsent()?.analytics) return;
  if (!canSend()) {
    pendingPage = path;
    void loadGa();
    return;
  }
  if (path === lastPagePath) return;
  lastPagePath = path;
  window.gtag?.("event", "page_view", {
    page_path: path,
    page_location: `${location.origin}${path}`,
    page_title: document.title,
  });
}

/** Allow the next navigation to the same path to count (called on every real route change). */
export function resetPageDedupe() {
  lastPagePath = null;
}

export function trackEvent(name: string, params: Record<string, string | number> = {}) {
  if (!canSend()) return;
  window.gtag?.("event", name, params);
}

/** Fires generate_lead once per submission id. Never pass personal data here. */
export function trackLead(submissionId: string, leadType: "kitchen" | "furniture" | "contact") {
  if (firedLeads.has(submissionId) || !readConsent()?.analytics) return;
  if (!canSend()) {
    pendingLeads.set(submissionId, leadType);
    void loadGa();
    return;
  }
  pendingLeads.delete(submissionId);
  firedLeads.add(submissionId);
  trackEvent("generate_lead", { lead_type: leadType });
}
